import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Calculator, Loader2, Save, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useFinance } from '@/contexts/FinanceContext';
import { useCardMachines } from './useCardMachines';
import { useCreateCardSplit } from './useCardSplits';
import { calculateSplit, pickMdrPct, PAYMENT_KIND_LABELS } from './types';
import type { CardPaymentKind } from './types';

function parseBrl(s: string): number {
  const cleaned = s
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function formatBrl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function SplitCalculator() {
  const { data: machines = [] } = useCardMachines();
  const { accounts, categories, costCenters } = useFinance();
  const create = useCreateCardSplit();

  const activeMachines = useMemo(() => machines.filter((m) => m.is_active), [machines]);
  const activeAccounts = useMemo(() => accounts.filter((a) => a.isActive), [accounts]);
  const incomeCategories = useMemo(
    () => categories.filter((c) => c.type === 'income'),
    [categories],
  );
  const activeCostCenters = useMemo(() => costCenters.filter((cc) => cc.isActive), [costCenters]);

  const today = format(new Date(), 'yyyy-MM-dd');
  const [machineId, setMachineId] = useState<string>('');
  const [paymentKind, setPaymentKind] = useState<CardPaymentKind>('credit_installments');
  const [installments, setInstallments] = useState('12');
  const [grossStr, setGrossStr] = useState('');
  const [transactionDate, setTransactionDate] = useState(today);
  const [anticipate, setAnticipate] = useState(true);
  const [overrideMdr, setOverrideMdr] = useState('');
  const [overrideAnticipation, setOverrideAnticipation] = useState('');
  const [description, setDescription] = useState('');
  const [accountId, setAccountId] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('none');
  const [costCenterId, setCostCenterId] = useState<string>('none');
  const [registerAsTransaction, setRegisterAsTransaction] = useState(true);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!machineId && activeMachines.length > 0 && activeMachines[0]) {
      setMachineId(activeMachines[0].id);
    }
  }, [activeMachines, machineId]);

  useEffect(() => {
    if (!machineId) return;
    const m = machines.find((x) => x.id === machineId);
    if (m?.default_account_id && !accountId) setAccountId(m.default_account_id);
  }, [machineId, machines, accountId]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const machine = useMemo(() => machines.find((m) => m.id === machineId), [machines, machineId]);
  const installmentsN = Math.max(1, parseInt(installments) || 1);
  const grossAmount = parseBrl(grossStr);

  const machineMdrPct = machine ? pickMdrPct(machine, paymentKind) : 0;
  const machineAnticipationPct = machine ? Number(machine.anticipation_rate_monthly_pct) : 0;
  const mdrPct = overrideMdr.trim() ? parseBrl(overrideMdr) : machineMdrPct;
  const anticipationPct = overrideAnticipation.trim()
    ? parseBrl(overrideAnticipation)
    : machineAnticipationPct;

  const result = useMemo(
    () =>
      calculateSplit({
        grossAmount,
        installments: installmentsN,
        paymentKind,
        mdrPct,
        anticipationRateMonthlyPct: anticipationPct,
        anticipate,
      }),
    [grossAmount, installmentsN, paymentKind, mdrPct, anticipationPct, anticipate],
  );

  const showInstallmentsField = paymentKind === 'credit_installments';

  const compare = useMemo(() => {
    if (grossAmount <= 0 || activeMachines.length === 0) return [];
    return activeMachines
      .map((m) => {
        const mPct = pickMdrPct(m, paymentKind);
        const r = calculateSplit({
          grossAmount,
          installments: installmentsN,
          paymentKind,
          mdrPct: mPct,
          anticipationRateMonthlyPct: Number(m.anticipation_rate_monthly_pct),
          anticipate,
        });
        return { machine: m, result: r };
      })
      .sort((a, b) => b.result.netAmount - a.result.netAmount);
  }, [activeMachines, grossAmount, installmentsN, paymentKind, anticipate]);

  const valid = !!machineId && grossAmount > 0;

  const handleSave = async () => {
    if (!valid || !machine) return;
    try {
      await create.mutateAsync({
        cardMachineId: machine.id,
        description: description || PAYMENT_KIND_LABELS[paymentKind],
        transactionDate,
        installments: showInstallmentsField ? installmentsN : 1,
        paymentKind,
        grossAmount,
        mdrPct,
        mdrAmount: result.mdrAmount,
        anticipationRateMonthlyPct: anticipationPct,
        anticipationAmount: result.anticipationAmount,
        netAmount: result.netAmount,
        accountId: accountId || null,
        categoryId: categoryId === 'none' ? null : categoryId,
        costCenterId: costCenterId === 'none' ? null : costCenterId,
        registerAsTransaction,
      });
      setGrossStr('');
      setDescription('');
    } catch {
      // toast já mostrou
    }
  };

  if (activeMachines.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-muted-foreground">
          <AlertCircle className="mx-auto mb-3 h-10 w-10 opacity-40" />
          <p className="text-sm">
            Cadastre pelo menos uma maquininha na aba "Maquininhas" para usar a calculadora.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {/* Form */}
      <Card className="lg:col-span-2">
        <CardContent className="space-y-4 p-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Maquininha</Label>
              <Select value={machineId} onValueChange={setMachineId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {activeMachines.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name}
                      {m.operator && <span className="text-muted-foreground"> · {m.operator}</span>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Forma de pagamento</Label>
              <Select
                value={paymentKind}
                onValueChange={(v) => setPaymentKind(v as CardPaymentKind)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit">{PAYMENT_KIND_LABELS.debit}</SelectItem>
                  <SelectItem value="credit_1x">{PAYMENT_KIND_LABELS.credit_1x}</SelectItem>
                  <SelectItem value="credit_installments">
                    {PAYMENT_KIND_LABELS.credit_installments}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label>Bruto *</Label>
              <Input
                placeholder="R$ 0,00"
                value={grossStr}
                onChange={(e) => setGrossStr(e.target.value)}
              />
            </div>
            {showInstallmentsField && (
              <div className="space-y-1.5">
                <Label>Parcelas</Label>
                <Input
                  type="number"
                  min="2"
                  max="24"
                  value={installments}
                  onChange={(e) => setInstallments(e.target.value)}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Data</Label>
              <Input
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
              />
            </div>
          </div>

          <div className="rounded-lg border border-border bg-secondary/30 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-semibold">Antecipação</span>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={anticipate}
                  onChange={(e) => setAnticipate(e.target.checked)}
                  className="h-4 w-4 rounded accent-primary"
                />
                Antecipar recebíveis
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">
                  MDR % (padrão: {machineMdrPct.toString().replace('.', ',')}%)
                </Label>
                <Input
                  placeholder="Override"
                  value={overrideMdr}
                  onChange={(e) => setOverrideMdr(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">
                  Antecipação %/mês (padrão: {machineAnticipationPct.toString().replace('.', ',')}%)
                </Label>
                <Input
                  placeholder="Override"
                  value={overrideAnticipation}
                  onChange={(e) => setOverrideAnticipation(e.target.value)}
                  disabled={!anticipate}
                />
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-3">
            <div className="mb-2 text-sm font-semibold">Registrar recebimento</div>
            <label className="mb-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={registerAsTransaction}
                onChange={(e) => setRegisterAsTransaction(e.target.checked)}
                className="h-4 w-4 rounded accent-primary"
              />
              Criar transação de receita (líquido) na conta
            </label>

            {registerAsTransaction && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Conta</Label>
                  <Select value={accountId} onValueChange={setAccountId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeAccounts.map((a) => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Categoria</Label>
                  <Select value={categoryId} onValueChange={setCategoryId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— sem categoria —</SelectItem>
                      {incomeCategories.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1.5">
                  <Label className="text-xs">Centro de custo</Label>
                  <Select value={costCenterId} onValueChange={setCostCenterId}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— sem centro de custo —</SelectItem>
                      {activeCostCenters.map((cc) => (
                        <SelectItem key={cc.id} value={cc.id}>
                          {cc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Descrição (opcional)</Label>
            <Input
              placeholder="Ex: Recebimento maquininha 30/04"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Resultado */}
      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Calculator size={14} className="text-primary" />
            Resultado
          </div>
          <div className="space-y-2 rounded-lg border border-primary/30 bg-primary/5 p-3">
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">Bruto</span>
              <span className="text-lg font-semibold tabular-nums">
                {formatBrl(result.grossAmount)}
              </span>
            </div>
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">
                MDR ({result.mdrPct.toString().replace('.', ',')}%)
              </span>
              <span className="text-sm tabular-nums text-expense">
                − {formatBrl(result.mdrAmount)}
              </span>
            </div>
            {anticipate && result.anticipationAmount > 0 && (
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-muted-foreground">
                  Antecipação ({result.anticipationRateMonthlyPct.toString().replace('.', ',')}%/m)
                </span>
                <span className="text-sm tabular-nums text-expense">
                  − {formatBrl(result.anticipationAmount)}
                </span>
              </div>
            )}
            <div className="flex items-baseline justify-between border-t border-primary/30 pt-2">
              <span className="text-sm font-semibold">Líquido</span>
              <span className="text-2xl font-bold tabular-nums text-income">
                {formatBrl(result.netAmount)}
              </span>
            </div>
            <p className="text-center text-xs text-muted-foreground">
              Custo efetivo: {result.effectiveCostPct.toFixed(2).replace('.', ',')}%
            </p>
          </div>

          <Button
            onClick={handleSave}
            disabled={!valid || create.isPending}
            className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {create.isPending ? (
              <>
                <Loader2 size={14} className="mr-2 animate-spin" />
                Registrando…
              </>
            ) : (
              <>
                <Save size={14} className="mr-2" />
                Registrar antecipação
              </>
            )}
          </Button>

          {compare.length > 1 && grossAmount > 0 && (
            <div className="border-t border-border pt-3">
              <div className="mb-2 text-xs font-semibold text-muted-foreground">
                Comparativo de maquininhas
              </div>
              <div className="space-y-1">
                {compare.map(({ machine: m, result: r }, idx) => (
                  <div
                    key={m.id}
                    className={`flex items-center justify-between rounded px-2 py-1 text-xs ${
                      idx === 0 ? 'bg-income/10 font-medium text-income' : 'text-foreground'
                    }`}
                  >
                    <span className="truncate">{m.name}</span>
                    <span className="tabular-nums">{formatBrl(r.netAmount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
